import { h, render, reactive } from "vue";
import { SIDEBAR_COMPONENTS } from "shared/constants/sidebar.constants";

// Drawer can access the app context
let appContext = null;

// HTML tag to render the drawer
const SELECTOR_TAG = "div";
export const CLOSE_ANIMATION_DURATION = 300;
export const CLOSE_ANIMATION_DURATION_IN_MS = `${CLOSE_ANIMATION_DURATION}ms`;

// Z-index layering for nested drawers
let drawerDepthCounter = 0;
export const acquireDrawerDepth = () => drawerDepthCounter++;
export const releaseDrawerDepth = () => drawerDepthCounter--;

// Track rendered sidebar drawer instances for stack management
const renderedSidebars = new Map();

/**
 * Reactive set of sidebar names currently in their close animation.
 * Other drawers watch this to shift back immediately when a drawer above
 * starts closing, instead of waiting for the URL to update after the animation.
 */
export const closingSidebars = reactive(new Set());

/**
 * Returns the map of currently rendered sidebar drawers.
 * Key = sidebar name, Value = { container, cleanup }
 */
export const getRenderedSidebars = () => renderedSidebars;

// Install function
export default {
  install: (app) => {
    appContext = app._context;
  },
};

export const openDrawer = () => {
  if (!appContext) {
    throw new Error(
      "openDrawer must be used after the drawer plugin is installed.",
    );
  }

  return ({ component, options }) => {
    return new Promise((resolve) => {
      // Create a new container to render the drawer
      const container = document.createElement(SELECTOR_TAG);

      // Add the container to the body
      document.body.appendChild(container);

      // Prevent scrolling on the body
      document.body.classList.add("tw-overflow-hidden");

      // Create the drawer component using the component and options
      const vnode = h(component, {
        ...options,
        onClose: () => {
          resolve();
          cleanup();
        },
      });

      // Pass the app context to the vnode
      vnode.appContext = appContext;

      // Cleanup function to remove the drawer
      // Note: the close animation already completed in DefaultDrawer.close()
      // before emit("close") triggers this, so no additional delay is needed.
      const cleanup = () => {
        document.body.classList.remove("tw-overflow-hidden");
        render(null, container);
        container.remove();

        // Remove from rendered sidebars tracking
        if (options?.sidebarName) {
          renderedSidebars.delete(options.sidebarName);
        }
      };

      // Track rendered sidebar for stack management
      if (options?.sidebarName) {
        renderedSidebars.set(options.sidebarName, { container, cleanup });
      }

      render(vnode, container);
    });
  };
};

// This composable can be used to open the drawer
export const useOpenDrawer = () => {
  const drawer = openDrawer();

  /**
   * Opens a single sidebar by name. Used by App.vue when the sidebar query param changes.
   * Now passes sidebarName for stack tracking.
   */
  const openSidebar = (sidebarName) => {
    const config = SIDEBAR_COMPONENTS[sidebarName];

    if (config === undefined) {
      return false;
    }

    // Clean up any stale entry from a previous render cycle.
    // This handles the case where DefaultDrawer.close() emits "close" on itself,
    // but the wrapper component (e.g. StudentAssignmentDrawer) claims the onClose
    // listener via defineEmits — so drawerHelper's cleanup never fires.
    const existing = renderedSidebars.get(sidebarName);

    if (existing) {
      existing.cleanup();
    }

    drawer({
      component: config.component,
      options: { ownsSidebar: true, sidebarName },
    });

    return true;
  };

  return { openDrawer: drawer, openSidebar };
};
